import { FC, useCallback, useEffect, useRef, useState } from "react";
import NavigationDrawer from "@/components/navigation/navigationDrawer";
import HeaderBar from "@/components/headerBar";
import {
  useAuthUser,
  useIsAuthenticated,
  useSignIn,
  useSignOut,
} from "react-auth-kit";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { api } from "@/helper/api";
import { setChildren } from "@/components/state/parent/childrenSlice";
import { setHasCourseEnrolments } from "@/components/state/parent/loginSlice";
import { RootState } from "@/components/state/store";
import "@/components/navigation/headerBarWrapper.css";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import {
  setManagerLmsAccessError,
  setManagerLmsAccessLoading,
  setManagerLmsAccessReady,
} from "@/components/state/lms/managerLmsAccessSlice";
import { normalizeLmsStartVertragSelection } from "@/components/lms/lmsQuickSearchEligibility";

interface Props {
  subdomain: string;
}
const HeaderBarWrapper: FC<Props> = ({ subdomain }) => {
  const [user, setUser] = useState<string | undefined>("");
  const [email, setEmail] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [loginstate, setLoginstate] = useState<number>(0);
  const [loginStateRefreshInterval, setLoginStateRefreshInterval]: any =
    useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const { handleError, errorModal } = useErrorModal();

  const openSettings = Boolean(anchorEl);

  const getAuth = useAuthUser();

  const signIn = useSignIn();
  const signOut = useSignOut();

  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedCompanySubjectId = useSelector(
    (state: RootState) => state.parent.subjectId
  );
  const defaultCompanySubjectId = useSelector(
    (state: RootState) => state.login.subjectId
  );
  const effectiveCompanySubjectId = String(
    (subdomain === "manage" ? selectedCompanySubjectId : null) ||
      defaultCompanySubjectId ||
      ""
  );
  const effectiveCompanySubjectIdRef = useRef(effectiveCompanySubjectId);
  effectiveCompanySubjectIdRef.current = effectiveCompanySubjectId;
  const latestMeRequestId = useRef(0);
  const managerCompaniesLoadedRef = useRef(false);

  const getEffectiveCompanySubjectId = useCallback(
    () => effectiveCompanySubjectIdRef.current,
    []
  );

  const startMeRequest = useCallback(() => {
    const request = {
      id: ++latestMeRequestId.current,
      subjectId: getEffectiveCompanySubjectId(),
    };
    if (subdomain === "manage" || subdomain === "portal") {
      dispatch(setManagerLmsAccessLoading(request.subjectId));
    }
    return request;
  }, [dispatch, getEffectiveCompanySubjectId, subdomain]);

  const isCurrentMeResponse = useCallback(
    (request: { id: number; subjectId: string }, responseData?: any) =>
      request.id === latestMeRequestId.current &&
      (!request.subjectId ||
        (request.subjectId === getEffectiveCompanySubjectId() &&
          (responseData === undefined ||
            request.subjectId === String(responseData?.subject_id ?? "")))),
    [getEffectiveCompanySubjectId]
  );

  const updateLmsAccess = useCallback(
    (responseData: any) => {
      if (subdomain !== "manage" && subdomain !== "portal") return;
      dispatch(
        setManagerLmsAccessReady({
          subjectId: String(responseData?.subject_id ?? ""),
          selection: normalizeLmsStartVertragSelection(responseData),
          hasCourseEnrolments:
            subdomain === "portal"
              ? responseData?.has_course_enrolments === true
              : null,
        })
      );
    },
    [dispatch, subdomain]
  );

  const toggleDrawer = (open: any) => (event: any) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };
  const combinedSignout = () => {
    dispatch({ type: "clear" });
    signOut();
  };
  useEffect(() => {
    if (subdomain === "manage" || subdomain === "portal") {
      const request = startMeRequest();
      api.genericApiRequest({
        entity: "me",
        method: "get",
        successHandler: (res: any) => {
          if (!isCurrentMeResponse(request, res.data)) return;
          updateLmsAccess(res.data);
          if (subdomain === "manage" && !managerCompaniesLoadedRef.current) {
            const tempCompanies = [];
            for (const key in res.data.assigned_companies ?? []) {
              const company = res.data.assigned_companies[key].company_subject;
              if (String(company.id) !== String(defaultCompanySubjectId)) {
                tempCompanies.push({
                  value: company.company_id,
                  label: company.name,
                  subjectId: company.id,
                });
              }
            }
            for (const key in res.data.child_companies ?? []) {
              const company = res.data.child_companies[key];
              tempCompanies.push({
                value: company.id,
                label: company.name,
                subjectId: company.subject_id,
              });
            }
            dispatch(setChildren(tempCompanies));
            managerCompaniesLoadedRef.current = true;
          }
        },
        failHandler: (error: any) => {
          if (!isCurrentMeResponse(request)) return;
          dispatch(setManagerLmsAccessError(request.subjectId));
          handleError(error);
        },
      });
    }
  }, [
    defaultCompanySubjectId,
    dispatch,
    handleError,
    isCurrentMeResponse,
    selectedCompanySubjectId,
    startMeRequest,
    subdomain,
    updateLmsAccess,
  ]);

  useEffect(() => {
    if (isAuthenticated()) {
      const auth = getAuth();

      if (auth && auth.email) {
        setLoginstate(1);
        setUser(auth.first_name + " " + auth.last_name);

        setEmail(auth.email);
        setCompanyName(auth.company_name);

        if (loginStateRefreshInterval === undefined) {
          setLoginStateRefreshInterval(
            setInterval(() => {
              console.log("refreshing login timeout...");
              const request = startMeRequest();
              api.genericApiRequest({
                entity: "me",
                method: "get",

                successHandler: (res: any) => {
                  if (res.status === 200) {
                    const responseData = res.data;
                    if (
                      (subdomain === "manage" || subdomain === "portal") &&
                      !isCurrentMeResponse(request, responseData)
                    ) {
                      return;
                    }
                    updateLmsAccess(responseData);
                    if (subdomain === "portal") {
                      dispatch(
                        setHasCourseEnrolments(
                          responseData?.has_course_enrolments === true
                        )
                      );
                    }
                    const sessionExpiration = Math.floor(
                      responseData.token_expiry_in / 60
                    );
                    if (sessionExpiration > 0) {
                      signIn({
                        token: responseData.email,
                        expiresIn: sessionExpiration,
                        tokenType: "Session",
                        authState: {
                          email: responseData.email,
                          first_name: responseData.first_name,
                          last_name: responseData.last_name,
                          company_name: responseData.company_name,
                        },
                      });
                    } else {
                      clearInterval(loginStateRefreshInterval);
                      setLoginstate(0);
                      setUser("");
                      setEmail("");
                      setCompanyName("");
                      setLoginStateRefreshInterval(undefined);

                      combinedSignout();
                    }
                  }
                },
                failHandler: (error: any, status: any) => {
                  if (subdomain === "manage" || subdomain === "portal") {
                    if (!isCurrentMeResponse(request)) return;
                    dispatch(setManagerLmsAccessError(request.subjectId));
                  }
                  if (status !== 504) {
                    handleError(error);
                  }
                },
              });
            }, 5 * 60 * 1000)
          );
        }
      }
    } else {
      if (loginstate === 1) {
        setLoginstate(0);
        setUser("");
        setEmail("");
        setCompanyName("");
        clearInterval(loginStateRefreshInterval);
        setLoginStateRefreshInterval(undefined);
        combinedSignout();
      }
    }
  }, [getAuth, isAuthenticated, loginstate]);

  let selectedCompany = companyName;
  const childName = useSelector((state: RootState) => state.parent.companyName);
  if (childName !== "") {
    selectedCompany = childName;
  }

  const nameParts = user?.split(" ");
  const firstNameInitial = nameParts?.[0] ? nameParts[0][0] : "";
  const lastNameInitial = nameParts?.[1] ? nameParts[1][0] : "";

  const handleOpenSettings = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseSettings = () => {
    setAnchorEl(null);
  };
  const handleNavigateSettings = () => {
    navigate("/usersettings");
    handleCloseSettings();
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  return (
    <>
      <div className="headerbar_wrapper">
        <HeaderBar
          toggleDrawer={toggleDrawer}
          selectedCompany={selectedCompany}
          handleOpenSettings={handleOpenSettings}
          handleCloseSettings={handleCloseSettings}
          handleNavigateSettings={handleNavigateSettings}
          combinedSignout={combinedSignout}
          signOut={signOut}
          openSettings={openSettings}
          firstNameInitial={firstNameInitial}
          lastNameInitial={lastNameInitial}
          anchorEl={anchorEl}
          user={user}
          email={email}
          subdomain={subdomain}
        />
        <NavigationDrawer
          drawerOpen={drawerOpen}
          toggleDrawer={toggleDrawer}
          subdomain={subdomain}
          closeDrawer={closeDrawer}
        />
      </div>
      {errorModal}
    </>
  );
};

export default HeaderBarWrapper;
